import { Card, Stack, Typography } from '@mui/joy';
import { Check, MagnifyingGlass } from '@phosphor-icons/react';
import { KeyboardEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, defaultRenderNewOption, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { isEnterKeyPress } from 'core/utils';
import {
  InventoryFilter,
  IPartDetailsPayload,
  SearchParts,
  usePartSearchFilters,
} from 'parts/components';
import { ALTERNATE_PART_NUMBER_MAX_LENGTH } from 'parts/constants';
import { ExternalPartStatusType } from 'parts/enums';
import { PartNumberHelper } from 'parts/helpers';
import { IPart, IPartAutocompleteRequestPayload, IPartsSearchRequestPayload } from 'parts/types';
import { Color, Level, Size, Variant } from 'theme/enums';

import { ExternalMarketingMemo } from './ExternalMarketingMemo';
import { ExternalPartStatus } from './ExternalPartStatus';

interface IProps {
  partSearchPayload?: IPartsSearchRequestPayload;
  partAutocompletePayload?: IPartAutocompleteRequestPayload;
  onChangePartNumber: (part: IPart | null) => void;
  onKeyDownPartNumber?: (event: KeyboardEvent<HTMLInputElement>, value: string) => void;
  isAlternateVersionEnabled?: boolean;
  disabled?: boolean;
  level?: number;
  partDetailsPayload?: IPartDetailsPayload;
  hasCreateOption?: boolean;
}

export const AdvancedPartNumberFormField = ({
  partSearchPayload = {},
  partAutocompletePayload = {},
  onChangePartNumber,
  onKeyDownPartNumber,
  isAlternateVersionEnabled,
  disabled,
  level = 0,
  partDetailsPayload,
  hasCreateOption = true,
}: IProps) => {
  const [partNumberInputValue, setPartNumberInputValue] = useState('');
  const [isPartNumberInputFocused, setIsPartNumberInputFocused] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  const { setFocusElementName } = useFocusInputFunctionality();

  const filtersState = usePartSearchFilters();
  const {
    filters: searchFilters,
    criteria: inventoryCriteria,
    onChangeInventoryFilter,
  } = filtersState;

  const partValue = watch('part');
  const manufacturerIdValue = watch('manufacturer')?.id;

  const autocompleteCriteria = useMemo(() => {
    if (!manufacturerIdValue && !inventoryCriteria) {
      return undefined;
    }

    return {
      ...(manufacturerIdValue && { manufacturerId: [manufacturerIdValue] }),
      ...inventoryCriteria,
    };
  }, [manufacturerIdValue, inventoryCriteria]);

  const isPartDisabled = (part: IPart & IAutocompleteOption) =>
    isAlternateVersionEnabled && part?.externalPartStatus === ExternalPartStatusType.ABANDONED;

  const onSearchPartsButtonClick = (inputValue: string) => {
    if (disabled) {
      return;
    }

    setIsPartNumberInputFocused(false);
    setPartNumberInputValue(inputValue);

    setFocusElementName('searchPart');
  };

  const handleOnKeyDownPartNumber = (event: KeyboardEvent<HTMLInputElement>, value: string) => {
    setPartNumberInputValue(value);
    onKeyDownPartNumber?.(event, value);
  };

  const onTryAdvancedSearchClick = () => {
    if (disabled) {
      return;
    }

    onSearchPartsButtonClick(partNumberInputValue);
    setIsSearchOpen(true);
  };

  const onCloseSearchParts = () => {
    setIsSearchOpen(false);
  };

  const getPartCardButtons = (part: IPart) => {
    const isDisabled = isPartDisabled(part);

    return [
      <Button
        key={part.id}
        variant={Variant.PLAIN}
        text={t('buttonLabelUsePartNumber')}
        onClick={() => {
          onCloseSearchParts();
          onChangePartNumber(part);
        }}
        startDecorator={<Check weight="bold" />}
        disabled={isDisabled}
      />,
    ];
  };

  return (
    <>
      <Card
        variant={Variant.OUTLINED}
        color={Color.PRIMARY}
        sx={{ backgroundColor: 'neutral.100', gap: 1.5 }}
      >
        <FormField
          key={PartNumberHelper.toValue(partValue?.number)}
          name={'part'}
          type={InputType.PART_AUTOCOMPLETE}
          inputProps={{
            control: control,
            error: errors?.part,
            label: t('labelPartNumber'),
            placeholder: t('placeholderUpsertLinePartNumber'),
            endDecorator: (
              <MagnifyingGlass
                onClick={() => !disabled && setIsSearchOpen(true)}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
              />
            ),
            onEndDecoratorClick: onSearchPartsButtonClick,
            requestPayload: {
              manufacturerIncluded: 1,
              alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
              ...(autocompleteCriteria && { criteria: autocompleteCriteria }),
              ...partAutocompletePayload,
            },
            onFocus: () => setIsPartNumberInputFocused(true),
            onChange: (
              event: KeyboardEvent<HTMLInputElement>,
              option: (IPart & IAutocompleteOption) | null
            ) => {
              if (isEnterKeyPress(event)) {
                event.preventDefault();

                return;
              }

              onChangePartNumber(option);
            },
            onKeyDown: isPartNumberInputFocused ? handleOnKeyDownPartNumber : null,
            renderNewOption: (option: IAutocompleteOption) =>
              defaultRenderNewOption(option, t('messageAddNewPartNumber'), {
                titleSx: { textTransform: 'uppercase' },
              }),
            getOptionDisabled: isPartDisabled,
            hasCreateOption,
            hideNewOptionIfMatched: false,
            disabled,
            isAlternateVersionEnabled,
            maxLength: isAlternateVersionEnabled ? ALTERNATE_PART_NUMBER_MAX_LENGTH : undefined,
            helperTexts: isAlternateVersionEnabled
              ? [
                  <ExternalPartStatus
                    key="externalPartStatus"
                    value={partValue?.externalPartStatus}
                  />,
                ]
              : [],
          }}
        />

        {isAlternateVersionEnabled && (
          <ExternalMarketingMemo marketingMemo={partValue?.marketingMemo} />
        )}

        <Stack
          spacing={1}
          sx={{
            opacity: disabled ? 0.5 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        >
          {isAlternateVersionEnabled ? (
            <InventoryFilter
              value={searchFilters.inventory}
              onChange={onChangeInventoryFilter}
              isAlternateVersionEnabled={isAlternateVersionEnabled}
              inline
              disabled={disabled}
            />
          ) : (
            <>
              <Typography level={Level.BODY_SM} sx={{ fontWeight: 500 }}>
                {t('messageDontKnowFullPartNumber')}
              </Typography>

              <Button
                text={t('buttonLabelTryAdvancedSearch')}
                variant={Variant.SOFT}
                color={Color.PRIMARY}
                size={Size.SM}
                onClick={onTryAdvancedSearchClick}
                disabled={disabled}
                sx={{ alignSelf: 'flex-start' }}
              />
            </>
          )}
        </Stack>
      </Card>

      {!disabled && (
        <SearchParts
          isOpen={isSearchOpen}
          onClose={onCloseSearchParts}
          defaultQuery={partNumberInputValue || PartNumberHelper.toLabel(partValue?.number) || ''}
          partSearchPayload={{
            alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
            ...partSearchPayload,
          }}
          getPartCardButtons={getPartCardButtons}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          level={level}
          partDetailsPayload={partDetailsPayload}
          filtersState={isAlternateVersionEnabled ? filtersState : undefined}
        />
      )}
    </>
  );
};
